import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FiSave, FiEye, FiEyeOff } from 'react-icons/fi';
import {
    getProvisioningDefaults, updateProvisioningDefaults, getEnvironmentTagOptions,
    ProvisioningDefaults, EnvironmentTagOptions, EnvironmentTagOptionsMap,
} from '../api/platformDefaultsApi';

type Env = 'prod' | 'dev' | 'demo';
const ENVS: Env[] = ['prod', 'dev', 'demo'];
const ENV_LABELS: Record<Env, string> = { prod: 'Production', dev: 'Development', demo: 'Demo' };

// Same formulas as TenantProvisionPage.computeUrls — shown here purely as a live preview so it's
// clear these URLs are calculated from Root Domain + Environment, not set directly per tenant.
const previewAppUrl = (rootDomain: string, env: Env) =>
    !rootDomain ? '' : (env === 'prod' ? `https://example.${rootDomain}` : `https://example.${env}.${rootDomain}`);
const previewKeycloakUrl = (rootDomain: string, env: Env) =>
    !rootDomain ? '' : (env === 'prod' ? `https://auth.${rootDomain}` : `https://auth.${env}.${rootDomain}`);
const previewCloudJwks = (namespace: string) =>
    !namespace ? '' : `http://identity-provider.${namespace}.svc.cluster.local`;
const previewLocalJwks = (host: string, port: number | string | undefined) =>
    !host ? '' : `http://${host}:${port || 8080}`;

const Schema = Yup.object().shape({
    keycloakAdminUrl: Yup.string().url('Must be a valid URL').required('Required'),
    jwksBaseUrl:      Yup.string().url('Must be a valid URL').required('Required'),
    jwksHost:         Yup.string().required('Required'),
    jwksPort:         Yup.number().integer().min(1).max(65535).required('Required'),
    // Tenant-provisioning wizard defaults are all optional — blank = fall back to
    // server/config/index.ts `tenantProvisioning` (env vars / Key Vault).
    jwksLocalPort: Yup.number().integer().min(1).max(65535).nullable(),
});

const EMPTY: ProvisioningDefaults = {
    keycloakAdminUrl: '',
    jwksBaseUrl:      '',
    jwksHost:         'identity-provider',
    jwksPort:         8080,
    rootDomainApp:    '',
    rootDomainLocal:  '',
    namespaces:       { prod: '', dev: '', demo: '' },
    environmentTags:  { prod: '', dev: '', demo: '' },
    jwksLocalHost:    '',
    jwksLocalPort:    undefined,
    secretStoreNonLocal:    '',
    secretStoreLocalAzure:  '',
    secretStoreLocalConsul: '',
    localConsulAddr: '',
    localVaultAddr:  '',
    azureAdIdp: {
        prod: { tenantId: '', clientId: '', clientSecret: '' },
        dev:  { tenantId: '', clientId: '', clientSecret: '' },
        demo: { tenantId: '', clientId: '', clientSecret: '' },
    },
};

const inputCls = 'mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm';
const sectionCls = 'bg-white dark:bg-gray-800 shadow rounded-lg p-6';
const previewCls = 'mt-2 rounded-md bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 px-3 py-2 text-xs font-mono text-primary-700 dark:text-primary-300 space-y-0.5';
const warnCls = 'mt-1 text-xs text-amber-600 dark:text-amber-400';
const tagLabelCls = 'block text-xs text-gray-500 dark:text-gray-400 mt-3';

// Constrained environment-tag picker. The options are the tags the environment's namespace actually
// loads, resolved SERVER-side by GET /provisioning-defaults/environment-tags — the precedence rule
// (override → config → derived) lives only in server/services/tenantProvisioningDefaults.ts and is
// never re-implemented here. When the resolved picture cannot be trusted (endpoint down, still
// loading, unknown namespace, or the namespace field edited but not yet saved) this falls back to
// the old free-text input rather than blocking the operator, and says why.
const EnvironmentTagField: React.FC<{
    env: Env;
    info?: EnvironmentTagOptions;
    unavailable: string | null;
    current: string;
    formNamespace: string;
}> = ({ env, info, unavailable, current, formNamespace }) => {
    const typedNamespace = (formNamespace || '').trim();
    let fallbackReason: string | null = null;
    if (unavailable) fallbackReason = unavailable;
    else if (!info) fallbackReason = 'the tag list has not loaded yet';
    else if (typedNamespace && typedNamespace !== info.namespace)
        fallbackReason = `the namespace was changed to "${typedNamespace}" — save to refresh the tag list`;
    else if (info.options.length === 0)
        fallbackReason = info.namespace
            ? `no tag list is known for namespace "${info.namespace}"`
            : 'no namespace is configured for this environment';

    const label = (
        <label className={tagLabelCls}>
            Environment tag <span className="text-gray-400">(blank = derive from the namespace above)</span>
        </label>
    );

    if (fallbackReason || !info) {
        return (
            <>
                {label}
                <Field name={`environmentTags.${env}`} type="text" className={inputCls} placeholder={`derived — normally "${env}"`} />
                {/* Still-loading is not yet a dangerous state — don't shout until it is. */}
                <p className={!unavailable && !info ? 'mt-1 text-xs text-gray-500 dark:text-gray-400' : warnCls}>
                    Tag picker unavailable — {fallbackReason}. Falling back to free text: enter only a tag this
                    namespace actually loads. The platform filters tenants with a Mongo
                    <code className="mx-1">$in</code> against the tags its namespace is deployed with, so a tag no
                    runtime loads makes every tenant provisioned for {ENV_LABELS[env]} silently invisible —
                    surfacing much later as an opaque platform-wide 401. Leave blank to derive.
                </p>
            </>
        );
    }

    // A value saved before this picker existed (or by an earlier free-text edit) may not be in the
    // list; keep it selectable so the field never silently rewrites it, and warn about it.
    const stale = current !== '' && !info.options.includes(current);

    return (
        <>
            {label}
            <Field as="select" name={`environmentTags.${env}`} className={inputCls}>
                <option value="">
                    {info.derived.tag
                        ? `Derive automatically — "${info.derived.tag}" (${info.derived.source})`
                        : 'Derive automatically — nothing resolves, no tag would be written'}
                </option>
                {info.options.map(tag => <option key={tag} value={tag}>{tag}</option>)}
                {stale && <option value={current}>{current} — not loaded by {info.namespace}</option>}
            </Field>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Namespace <span className="font-mono">{info.namespace}</span> loads{' '}
                <span className="font-mono">{info.options.join(', ')}</span> · in force:{' '}
                <span className="font-mono">{info.resolved || '—'}</span> ({info.source})
            </p>
            {stale && (
                <p className={warnCls}>
                    <span className="font-mono">{current}</span> is not a tag namespace{' '}
                    <span className="font-mono">{info.namespace}</span> loads. The platform filters tenants with a
                    Mongo <code className="mx-1">$in</code> against the tags its namespace is deployed with, so every
                    tenant provisioned for {ENV_LABELS[env]} carries a tag no runtime matches and is silently
                    invisible — surfacing much later as an opaque platform-wide 401. Pick a listed tag, or derive.
                </p>
            )}
        </>
    );
};

const ProvisioningDefaultsPage: React.FC = () => {
    const [initial, setInitial] = useState<ProvisioningDefaults>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showSecret, setShowSecret] = useState<Partial<Record<Env, boolean>>>({});
    const [tagOptions, setTagOptions] = useState<EnvironmentTagOptionsMap | null>(null);
    const [tagOptionsError, setTagOptionsError] = useState<string | null>(null);

    // Environment-tag picker options, resolved server-side. Deliberately separate from the defaults
    // load: a failure here degrades the field to free text, it must not block or fail the page.
    useEffect(() => {
        getEnvironmentTagOptions()
            .then(setTagOptions)
            .catch(e => setTagOptionsError(e?.message ?? 'the tag list could not be loaded'));
    }, []);

    useEffect(() => {
        getProvisioningDefaults()
            // Deep-merge over EMPTY — a document saved before the tenant-provisioning fields
            // existed won't have the nested namespaces/environmentTags/azureAdIdp objects yet.
            .then((d: ProvisioningDefaults) => setInitial({
                ...EMPTY,
                ...d,
                namespaces: {
                    prod: d?.namespaces?.prod ?? '',
                    dev:  d?.namespaces?.dev  ?? '',
                    demo: d?.namespaces?.demo ?? '',
                },
                environmentTags: {
                    prod: d?.environmentTags?.prod ?? '',
                    dev:  d?.environmentTags?.dev  ?? '',
                    demo: d?.environmentTags?.demo ?? '',
                },
                azureAdIdp: {
                    prod: { tenantId: d?.azureAdIdp?.prod?.tenantId ?? '', clientId: d?.azureAdIdp?.prod?.clientId ?? '', clientSecret: d?.azureAdIdp?.prod?.clientSecret ?? '' },
                    dev:  { tenantId: d?.azureAdIdp?.dev?.tenantId  ?? '', clientId: d?.azureAdIdp?.dev?.clientId  ?? '', clientSecret: d?.azureAdIdp?.dev?.clientSecret  ?? '' },
                    demo: { tenantId: d?.azureAdIdp?.demo?.tenantId ?? '', clientId: d?.azureAdIdp?.demo?.clientId ?? '', clientSecret: d?.azureAdIdp?.demo?.clientSecret ?? '' },
                },
            }))
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    const handleSubmit = async (values: ProvisioningDefaults) => {
        try {
            const saved = await updateProvisioningDefaults(values);
            setInitial(saved);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (e: any) {
            setError(e.message ?? 'Save failed');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Provisioning Defaults</h1>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Config-driven defaults for the Provision Tenant wizard. Blank fields fall back to the server's env/Key Vault configuration.
                    </p>
                </div>
            </div>

            {error && (
                <div className="rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 p-4 text-sm text-red-600 dark:text-red-300">
                    {error}
                </div>
            )}
            {saved && (
                <div className="rounded-md bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 p-4 text-sm text-green-700 dark:text-green-300">
                    Defaults saved successfully.
                </div>
            )}

            <Formik initialValues={initial} validationSchema={Schema} onSubmit={handleSubmit} enableReinitialize>
                {({ isValid, dirty, isSubmitting, values }) => (
                    <Form className="space-y-6">

                        {/* ── Local / On-Prem ─────────────────────────────────────────── */}
                        <div className={sectionCls}>
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">🏠 Local / On-Prem</h3>
                            <p className="text-xs text-gray-400 mb-4">
                                Used when the "Local (on-prem/dev)" checkbox is checked in the Provision Tenant wizard — same Keycloak/App URL formula as Cloud below, just against this root domain.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Root Domain <span className="text-xs text-gray-400">(e.g. ontoris.local)</span>
                                    </label>
                                    <Field name="rootDomainLocal" type="text" className={inputCls} placeholder="ontoris.local" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">JWKS Host : Port</label>
                                    <div className="mt-1 grid grid-cols-2 gap-2">
                                        <Field name="jwksLocalHost" type="text" className={inputCls} placeholder="identity-provider" />
                                        <Field name="jwksLocalPort" type="number" className={inputCls} placeholder="8080" />
                                    </div>
                                    <ErrorMessage name="jwksLocalPort" component="p" className="mt-1 text-xs text-red-500" />
                                </div>
                            </div>

                            <div className={previewCls}>
                                <p>Keycloak (example, dev): {previewKeycloakUrl(values.rootDomainLocal || 'ontoris.local', 'dev')}</p>
                                <p>App URL (example, dev): {previewAppUrl(values.rootDomainLocal || 'ontoris.local', 'dev')}</p>
                                <p>JWKS: {previewLocalJwks(values.jwksLocalHost || 'identity-provider', values.jwksLocalPort || 8080)}</p>
                            </div>

                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-4 mb-2">Secret store</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400">Config type: Azure</label>
                                    <Field name="secretStoreLocalAzure" type="text" className={inputCls} placeholder="ontoriskeyvault" />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400">Config type: Local (Consul + Vault)</label>
                                    <Field name="secretStoreLocalConsul" type="text" className={inputCls} placeholder="secretstore" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Consul address</label>
                                    <Field name="localConsulAddr" type="text" className={inputCls} placeholder="http://consul:8500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Vault address</label>
                                    <Field name="localVaultAddr" type="text" className={inputCls} placeholder="https://vault:8200" />
                                </div>
                            </div>
                        </div>

                        {/* ── Cloud ────────────────────────────────────────────────────── */}
                        <div className={sectionCls}>
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">☁️ Cloud</h3>
                            <p className="text-xs text-gray-400 mb-4">
                                Used when "Local" is unchecked. Keycloak and App URLs are calculated from Root Domain + Environment — not set directly. JWKS is calculated from the environment's in-cluster namespace.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Root Domain <span className="text-xs text-gray-400">(e.g. ontoris.app)</span>
                                    </label>
                                    <Field name="rootDomainApp" type="text" className={inputCls} placeholder="ontoris.app" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Secret store</label>
                                    <Field name="secretStoreNonLocal" type="text" className={inputCls} placeholder="ontoris-nonprod-akv" />
                                </div>
                            </div>

                            <div className="space-y-4">
                                {ENVS.map(env => (
                                    <div key={env} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">{ENV_LABELS[env]}</p>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400">
                                            In-cluster namespace <span className="text-gray-400">(drives JWKS below)</span>
                                        </label>
                                        <Field name={`namespaces.${env}`} type="text" className={inputCls} placeholder={env === 'dev' ? 'app-dev' : 'app-demo'} />
                                        <EnvironmentTagField
                                            env={env}
                                            info={tagOptions?.[env]}
                                            unavailable={tagOptionsError}
                                            current={values.environmentTags?.[env] ?? ''}
                                            formNamespace={values.namespaces?.[env] ?? ''}
                                        />
                                        <div className={previewCls}>
                                            <p>Keycloak: {previewKeycloakUrl(values.rootDomainApp || 'ontoris.app', env)}</p>
                                            <p>App URL (example): {previewAppUrl(values.rootDomainApp || 'ontoris.app', env)}</p>
                                            <p>JWKS: {previewCloudJwks(values.namespaces?.[env] || (env === 'dev' ? 'app-dev' : 'app-demo'))}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ── Azure AD Federation ──────────────────────────────────────── */}
                        <div className={sectionCls}>
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">🔑 Azure AD Federation</h3>
                            <p className="text-xs text-gray-400 mb-4">
                                Pre-populates the Federated Identity Provider step in the wizard when Azure AD is selected — applies regardless of Local/Cloud. Client secret overrides the Key Vault secret when set.
                            </p>

                            <div className="space-y-4">
                                {ENVS.map(env => (
                                    <div key={env} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">{ENV_LABELS[env]}</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-xs text-gray-500 dark:text-gray-400">Azure Tenant ID</label>
                                                <Field name={`azureAdIdp.${env}.tenantId`} type="text" className={inputCls} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-500 dark:text-gray-400">Client ID</label>
                                                <Field name={`azureAdIdp.${env}.clientId`} type="text" className={inputCls} placeholder="App Registration client ID" />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-500 dark:text-gray-400">Client Secret</label>
                                                <div className="relative">
                                                    <Field name={`azureAdIdp.${env}.clientSecret`} type={showSecret[env] ? 'text' : 'password'} className={`${inputCls} pr-9`} placeholder="Leave blank to keep using the Key Vault secret" />
                                                    <button type="button"
                                                        onClick={() => setShowSecret(s => ({ ...s, [env]: !s[env] }))}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                                        {showSecret[env] ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ── Reconcile Fallback (legacy) ──────────────────────────────── */}
                        <div className={sectionCls}>
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Reconcile Fallback</h3>
                            <p className="text-xs text-gray-400 mb-4">
                                Only used by Keycloak Reconcile on a legacy tenant that has no Keycloak config stored on its own document yet — unrelated to the Provision Tenant wizard above.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Keycloak Admin URL <span className="text-xs text-gray-400">(e.g. http://keycloak:8080)</span>
                                    </label>
                                    <Field name="keycloakAdminUrl" type="text" className={inputCls} placeholder="http://keycloak:8080" />
                                    <ErrorMessage name="keycloakAdminUrl" component="p" className="mt-1 text-xs text-red-500" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        JWKS Base URL <span className="text-xs text-gray-400">(e.g. http://identity-provider:8080)</span>
                                    </label>
                                    <Field name="jwksBaseUrl" type="text" className={inputCls} placeholder="http://identity-provider:8080" />
                                    <ErrorMessage name="jwksBaseUrl" component="p" className="mt-1 text-xs text-red-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        JWKS Host <span className="text-xs text-gray-400">(fallback)</span>
                                    </label>
                                    <Field name="jwksHost" type="text" className={inputCls} placeholder="identity-provider" />
                                    <ErrorMessage name="jwksHost" component="p" className="mt-1 text-xs text-red-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        JWKS Port <span className="text-xs text-gray-400">(fallback)</span>
                                    </label>
                                    <Field name="jwksPort" type="number" className={inputCls} />
                                    <ErrorMessage name="jwksPort" component="p" className="mt-1 text-xs text-red-500" />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={!(isValid && dirty) || isSubmitting}
                                className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FiSave className="h-4 w-4" />
                                {isSubmitting ? 'Saving…' : 'Save Defaults'}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default ProvisioningDefaultsPage;
